import React, { useState, useEffect } from 'react';
import { useCart } from '../useCart'; 
import api from '../../../utils/api';

const MEAT_CATEGORIES = ['All', 'Mutton', 'Chicken', 'Other'];

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BASE_URL || 'http://localhost:4000'}${url}`;
};

export default function BrowseMeatPanel({ user, onOpenCart, onBuyNow }) {
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [category,  setCategory]  = useState('All');
  const [minPrice,  setMinPrice]  = useState('');
  const [maxPrice,  setMaxPrice]  = useState('');
  const [selected,  setSelected]  = useState(null);
  const { addToCart, isInCart, flushSync } = useCart(user);
  const [inquiryProduct,  setInquiryProduct]  = useState(null);
  const [inquiryMsg,      setInquiryMsg]      = useState('');
  const [inquirySent,     setInquirySent]     = useState(false);
  const [inquiryLoading,  setInquiryLoading]  = useState(false);
  const [qty, setQty] = useState(1);
  const [quickBuy, setQuickBuy] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== 'All') params.category  = category;
      if (search)             params.search     = search;
      if (minPrice)           params.min_price  = minPrice;
      if (maxPrice)           params.max_price  = maxPrice;
      const { data } = await api.get('/api/public/browse-meat', { params });
      setProducts(data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const handleSendInquiry = async () => {
    if (!inquiryMsg.trim()) return;
    setInquiryLoading(true);
    try {
        await api.post('/api/customer/inquiries/meat', {
        product_id: inquiryProduct.id,
        seller_id:  inquiryProduct.butcher_id,
        message:    inquiryMsg,
        },);
        setInquirySent(true);
        setInquiryMsg('');
    } catch {
        alert('Could not send inquiry. Please login first.');
    } finally { setInquiryLoading(false); }
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProducts(); }, [category]);

  return (
    <>
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-black text-on-surface">Browse Meat Products</h2>
        <p className="text-sm text-secondary mt-1">Fresh meat products from verified butchers.</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-outline-variant/10">
        <form onSubmit={e => { e.preventDefault(); fetchProducts(); }}
              className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">Search</label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-[18px]">search</span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Product name..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-outline-variant/40 text-sm outline-none focus:border-primary transition-all" />
            </div>
          </div>
          <div className="w-32">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">Min ₹</label>
            <input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="0"
              className="w-full px-3 py-2.5 rounded-xl border border-outline-variant/40 text-sm outline-none focus:border-primary transition-all" />
          </div>
          <div className="w-32">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">Max ₹</label>
            <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="Any"
              className="w-full px-3 py-2.5 rounded-xl border border-outline-variant/40 text-sm outline-none focus:border-primary transition-all" />
          </div>
          <button type="submit" className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition-all">
            Search
          </button>
          <button type="button" onClick={() => { setSearch(''); setMinPrice(''); setMaxPrice(''); setCategory('All'); }}
            className="px-4 py-2.5 rounded-xl border border-outline-variant text-secondary text-sm font-semibold hover:bg-surface-container transition-all">
            Reset
          </button>
        </form>
        <div className="flex gap-2 mt-3 flex-wrap">
          {MEAT_CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                category === c ? 'bg-primary text-white' : 'bg-surface-container text-secondary hover:bg-surface-container-high'
              }`}>{c}</button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-outline-variant/10 animate-pulse">
              <div className="h-48 bg-surface-container" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-surface-container rounded w-3/4" />
                <div className="h-3 bg-surface-container rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-2xl p-14 shadow-sm border border-outline-variant/10 flex flex-col items-center text-center gap-3">
          <span className="material-symbols-outlined text-[48px] text-outline">storefront</span>
          <p className="text-lg font-black text-on-surface">No products found</p>
          <p className="text-sm text-secondary">Try adjusting your filters.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-secondary font-semibold">{products.length} product{products.length !== 1 ? 's' : ''} found</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map(p => (
              <div key={p.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-outline-variant/10 hover:shadow-md hover:border-primary/20 transition-all cursor-pointer group"
                onClick={() => setSelected(p)}>
                <div className="h-48 bg-surface-container overflow-hidden relative">
                  {p.image_url
                    ? <img src={getImageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    : <div className="w-full h-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-[48px] text-outline">storefront</span>
                      </div>
                  }
                  <span className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm text-[10px] font-black px-2 py-1 rounded-full text-primary">
                    {p.category}
                  </span>
                  <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-1 rounded-full ${
                    p.stock_qty > 0 ? 'bg-black/60 text-white' : 'bg-red-500 text-white'
                    }`}>
                    {p.stock_qty > 0 ? `${p.stock_qty} left` : 'Out of Stock'}
                    </span>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start gap-2">
                    <p className="font-black text-sm text-on-surface leading-tight">{p.name}</p>
                    <p className="text-primary font-black text-sm whitespace-nowrap">₹{Number(p.price_inr).toLocaleString('en-IN')}</p>
                  </div>
                  <p className="text-xs text-secondary mt-1">{p.weight_kg ? `${p.weight_kg} ${p.unit || 'kg'}` : p.unit || '—'}</p>
                  {(p.location_city || p.location_state) && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-secondary">
                      <span className="material-symbols-outlined text-[13px]">location_on</span>
                      {[p.location_city, p.location_state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-2 mt-3">
                    {p.stock_qty <= 0 ? (
                      <button disabled
                        className="w-full sm:flex-1 py-2 rounded-xl text-xs font-bold
                                  bg-orange-50 text-orange-400 border border-orange-100 cursor-not-allowed">
                        Out of Stock
                      </button>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setQuickBuy(p); }}
                        className="w-full sm:flex-1 py-2 rounded-xl text-xs font-bold bg-primary text-white
                                  hover:opacity-90 transition-all flex items-center justify-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">shopping_cart</span>
                        Buy / Add
                      </button>
                    )}
                    <button
                      onClick={e => { e.stopPropagation(); setSelected(p); }}
                      className="w-full sm:flex-1 sm:px-3 py-2 rounded-xl border border-outline-variant text-secondary
                                text-xs font-bold hover:bg-surface-container transition-all">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>

    {/* Quick-Buy Popup */}
    {quickBuy && (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center
                      justify-center sm:px-4"
          onClick={() => { setQuickBuy(null); setQty(1); }}>
        <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl
                        shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-outline-variant/10">
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-surface-container
                            flex-shrink-0 flex items-center justify-center">
              {quickBuy.image_url ? (
                <img src={getImageUrl(quickBuy.image_url)}
                    alt={quickBuy.name} className="w-full h-full object-cover" />
              ) : (
                <span className="material-symbols-outlined text-[28px] text-outline">storefront</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-sm text-on-surface truncate">{quickBuy.name}</p>
              <p className="text-xs text-secondary">{quickBuy.category} · {quickBuy.weight_kg ? `${quickBuy.weight_kg} ${quickBuy.unit || 'kg'}` : quickBuy.unit || '—'}</p>
              <p className="text-base font-black text-primary mt-0.5">
                ₹{Number(quickBuy.price_inr).toLocaleString('en-IN')}
              </p>
              {qty > 1 && (
                <p className="text-xs text-emerald-700 font-bold bg-emerald-50 px-2 py-1 rounded-lg mt-1">
                  ₹{Number(quickBuy.price_inr).toLocaleString('en-IN')} × {qty} = ₹{(Number(quickBuy.price_inr) * qty).toLocaleString('en-IN')}
                </p>
              )}
            </div>
            <button onClick={() => { setQuickBuy(null); setQty(1); }}
              className="w-7 h-7 rounded-full bg-surface-container flex items-center
                        justify-center text-secondary flex-shrink-0 self-start">
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>

          {/* Quantity + Actions */}
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <p className="text-xs font-black uppercase tracking-widest text-secondary w-8">Qty</p>
              <div className="flex items-center gap-2 bg-surface-container rounded-xl px-3 py-1.5">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  disabled={qty <= 1}
                  className="w-7 h-7 flex items-center justify-center text-lg font-bold
                            text-secondary hover:text-on-surface disabled:opacity-30 transition-all">
                  −
                </button>
                <span className="w-8 text-center font-black text-sm">{qty}</span>
                <button onClick={() => setQty(q => Math.min(quickBuy.stock_qty || 99, q + 1))}
                  disabled={qty >= (quickBuy.stock_qty || 99)}
                  className="w-7 h-7 flex items-center justify-center text-lg font-bold
                            text-secondary hover:text-on-surface disabled:opacity-30 transition-all">
                  +
                </button>
              </div>
              {qty >= quickBuy.stock_qty && (
                <p className="text-[10px] text-orange-600 font-semibold">
                  Only {quickBuy.stock_qty} available
                </p>
              )}
              {quickBuy.location_city && (
                <span className="text-[10px] text-secondary flex items-center gap-0.5">
                  <span className="material-symbols-outlined text-[12px]">location_on</span>
                  {quickBuy.location_city}
                </span>
              )}
            </div>

            {String(user?.id) === String(quickBuy.butcher_id) ? (
              <div className="bg-surface-container rounded-xl px-4 py-3 text-sm text-secondary text-center">
                This is your own listing.
              </div>
            ) : (
              <>
                {isInCart(`meat_${quickBuy.id}`) ? (
                  <div className="w-full py-2.5 rounded-xl text-sm font-bold text-center bg-emerald-100 text-emerald-700 border border-emerald-200">
                    ✓ Already in Cart
                  </div>
                ) : (
                  <button onClick={() => { addToCart({ ...quickBuy, id: `meat_${quickBuy.id}`, _type: 'meat' }, qty); setQty(1); }}
                    className="w-full py-2.5 rounded-xl border-2 border-primary text-primary text-sm font-bold hover:bg-primary/5 transition-all flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-[18px]">shopping_cart</span>
                    Add to Cart
                  </button>
                )}
                <button onClick={async () => {
                    if (!isInCart(`meat_${quickBuy.id}`)) addToCart({ ...quickBuy, id: `meat_${quickBuy.id}`, _type: 'meat' }, qty);
                    setQuickBuy(null); setQty(1);
                    await flushSync();
                    onBuyNow?.();
                  }}
                  className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[18px]">bolt</span>
                  Buy Now
                </button>
              </>
            )}

            <button
              onClick={() => { setSelected(quickBuy); setQuickBuy(null); }}
              className="w-full py-2 text-xs text-secondary font-semibold
                        hover:text-on-surface transition-all text-center">
              View full details →
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Detail Modal */}
    {selected && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-3" onClick={() => setSelected(null)}>
        <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl flex flex-col" style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
          <div className="h-48 bg-surface-container relative rounded-t-2xl overflow-hidden flex-shrink-0">
            {selected.image_url
              ? <img src={getImageUrl(selected.image_url)} alt={selected.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-[64px] text-outline">storefront</span>
                </div>
            }
            <button onClick={() => setSelected(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
          <div className="p-4 space-y-3 overflow-y-auto">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-base font-black text-on-surface">{selected.name}</h3>
                <p className="text-xs text-secondary">{selected.category}</p>
              </div>
              <p className="text-lg font-black text-primary">₹{Number(selected.price_inr).toLocaleString('en-IN')}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { icon: 'scale',       label: 'Weight', val: selected.weight_kg ? `${selected.weight_kg} ${selected.unit || 'kg'}` : '—' },
                { icon: 'inventory',   label: 'Stock',  val: selected.stock_qty ?? '—' },
                { icon: 'location_on', label: 'City',   val: selected.location_city || '—' },
              ].map(({ icon, label, val }) => (
                <div key={label} className="bg-surface-container-low rounded-xl p-2 text-center">
                  <span className="material-symbols-outlined text-primary text-[16px]">{icon}</span>
                  <p className="text-[9px] text-secondary font-bold uppercase mt-0.5">{label}</p>
                  <p className="text-xs font-black text-on-surface">{val}</p>
                </div>
              ))}
            </div>
            {selected.description && (
              <div className="bg-surface-container-low rounded-xl p-3">
                <p className="text-[10px] font-black uppercase text-secondary mb-1">Description</p>
                <p className="text-sm text-on-surface">{selected.description}</p>
              </div>
            )}
            {selected.seller_name && (
              <div className="flex items-center gap-2 bg-red-50 rounded-xl p-3">
                <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white font-black text-sm">
                  {selected.seller_name[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-black">{selected.seller_name}</p>
                  <p className="text-[10px] text-secondary">Verified Butcher</p>
                </div>
              </div>
            )}
            <div className="flex gap-3 items-end">
              {selected.stock_qty <= 0 ? (
                <button disabled
                  className="flex-1 py-2.5 text-xs rounded-xl font-bold flex items-center justify-center gap-2 bg-orange-50 text-orange-400 border border-orange-100 cursor-not-allowed">
                  <span className="material-symbols-outlined text-[18px]">shopping_cart_off</span>
                  Out of Stock
                </button>
              ) : String(user?.id) === String(selected.butcher_id) ? (
                <div className="flex-1 bg-surface-container rounded-xl px-3 py-2.5 text-xs text-secondary text-center">
                  Your own listing
                </div>
              ) : (
              <div className="flex-1 flex flex-col gap-2">
                {!isInCart(`meat_${selected.id}`) && (
                  <>
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))}
                        className="w-8 h-8 rounded-full border border-outline-variant/30
                                  flex items-center justify-center font-bold text-secondary
                                  hover:bg-surface-container transition-all">−</button>
                      <span className="w-8 text-center font-bold text-sm">{qty}</span>
                      <button onClick={() => setQty(q => Math.min(selected.stock_qty || 99, q + 1))}
                        className="w-8 h-8 rounded-full border border-outline-variant/30
                                  flex items-center justify-center font-bold text-secondary
                                  hover:bg-surface-container transition-all">+</button>
                    </div>
                    {qty >= selected.stock_qty && (
                      <p className="text-center text-[10px] text-orange-600 font-semibold">
                        Only {selected.stock_qty} available
                      </p>
                    )}
                </>
                )}
                {!isInCart(`meat_${selected.id}`) && qty > 1 && (
                  <p className="text-center text-xs text-emerald-700 font-bold bg-emerald-50 px-3 py-1 rounded-lg">
                    ₹{Number(selected.price_inr).toLocaleString('en-IN')} × {qty} = ₹{(Number(selected.price_inr) * qty).toLocaleString('en-IN')}
                  </p>
                )}
                <button onClick={() => { addToCart({ ...selected, id: `meat_${selected.id}`, _type: 'meat' }, qty); setQty(1); }}
                    className={`flex-1 py-2.5 text-xs rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                    isInCart(`meat_${selected.id}`)
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'border-2 border-primary text-primary hover:bg-primary/5'
                    }`}>
                    <span className="material-symbols-outlined text-[18px]">shopping_cart</span>
                    {isInCart(`meat_${selected.id}`) ? 'Added to Cart' : 'Add to Cart'}
                </button>
              </div>
              )}
                <button onClick={() => { setInquiryProduct(selected); setInquirySent(false); }}
                    className="flex-1 py-2.5 text-xs rounded-xl bg-primary text-white font-bold
                            hover:opacity-90 transition-all flex items-center justify-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">forum</span>
                    Inquiry
                </button>
                </div>
          </div>
        </div>
      </div>
    )}

    {/* Inquiry Modal */}
    {inquiryProduct && (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => { setInquiryProduct(null); setInquirySent(false); }}>
        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4"
            onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start">
            <div>
            <h3 className="font-black text-lg">Send Inquiry</h3>
            <p className="text-sm text-secondary">{inquiryProduct.name}</p>
            </div>
            <button onClick={() => { setInquiryProduct(null); setInquirySent(false); }}
            className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center">
            <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
        </div>
        {inquirySent ? (
            <div className="text-center py-6 space-y-2">
            <span className="material-symbols-outlined text-[48px] text-emerald-500">check_circle</span>
            <p className="font-black text-on-surface">Inquiry Sent!</p>
            <p className="text-sm text-secondary">The butcher will reply soon.</p>
            <button onClick={() => { setInquiryProduct(null); setInquirySent(false); }}
                className="mt-2 px-6 py-2 rounded-xl bg-primary text-white font-bold text-sm">Done</button>
            </div>
        ) : (
            <>
            <textarea value={inquiryMsg} onChange={e => setInquiryMsg(e.target.value)}
                placeholder="Ask about quantity, delivery, pricing..." rows={4}
                className="w-full px-4 py-3 rounded-xl border border-outline-variant/40 text-sm
                        outline-none focus:border-primary resize-none transition-all" />
            <button onClick={handleSendInquiry} disabled={inquiryLoading || !inquiryMsg.trim()}
                className="w-full py-3 rounded-xl bg-primary text-white font-bold
                        hover:opacity-90 disabled:opacity-50 transition-all">
                {inquiryLoading ? 'Sending...' : 'Send Message'}
            </button>
            </>
        )}
        </div>
    </div>
    )}
    </>
  );
}